/**
 * Smart scroll overlay — "new content" button for the chat panel.
 *
 * When the user scrolls up during a streaming response the overlay appears,
 * allowing them to jump back to the latest content.  Patches the internal
 * `shouldBlockStreamAutoScroll` so auto-scroll only happens while the user
 * follows the latest message. New turns resume following; scrolling up pauses it.
 */

import { queryAllAcrossRoots } from './domUtils.js';

interface InternalChatPresentation {
  shouldBlockStreamAutoScroll: () => boolean;
}

interface InternalChatDrawer {
  scrollToBottomPresentation?: (behavior?: ScrollBehavior) => void;
  setPresentationFocus?: (threadId: string | null) => void;
  setFormerMessagesButtonVisible?: (visible: boolean) => void;
}

interface InternalChatWidget {
  _presentation?: InternalChatPresentation;
  _drawer?: InternalChatDrawer;
}

const NEW_CONTENT_BUTTON_BOTTOM_OFFSET = 92;

export function mountSmartScrollOverlay(chat: unknown): () => void {
  const internalChat = chat as InternalChatWidget | null;
  if (!internalChat) {
    return () => {};
  }

  let followLatest = true;
  const presentation = internalChat._presentation;
  let restorePresentationPatch = () => {};
  const drawer = internalChat._drawer;
  const originalSetFocus = drawer?.setPresentationFocus;
  const originalSetFormerMessagesVisible = drawer?.setFormerMessagesButtonVisible;

  // SDK 0.7.18 collapses older turns on every send and exposes no config switch.
  // Keep the full transcript while preserving its scroll and panel navigation state.
  if (drawer && originalSetFocus && originalSetFormerMessagesVisible) {
    drawer.setPresentationFocus = () => originalSetFocus.call(drawer, null);
    drawer.setFormerMessagesButtonVisible = () =>
      originalSetFormerMessagesVisible.call(drawer, false);
    drawer.setPresentationFocus(null);
    drawer.setFormerMessagesButtonVisible(false);
  }

  if (presentation && typeof presentation.shouldBlockStreamAutoScroll === 'function') {
    const originalShouldBlock = presentation.shouldBlockStreamAutoScroll;
    presentation.shouldBlockStreamAutoScroll = () => !followLatest;
    restorePresentationPatch = () => {
      presentation.shouldBlockStreamAutoScroll = originalShouldBlock;
    };
  }

  let currentScrollEl: HTMLElement | null = null;
  let detachFromScrollEl = () => {};

  const attachScrollOverlay = (scrollEl: HTMLElement): (() => void) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.gengagePart = 'chat-new-content-button';
    button.textContent = '↓ Yeni yanıtları gör';
    Object.assign(button.style, {
      position: 'sticky',
      bottom: `${NEW_CONTENT_BUTTON_BOTTOM_OFFSET}px`,
      zIndex: '4',
      margin: '0 auto 4px',
      padding: '10px 15px',
      borderRadius: '999px',
      border: '1px solid rgba(15, 43, 91, 0.24)',
      background:
        'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(243, 248, 255, 0.98) 100%)',
      color: '#0f2b5b',
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '1',
      boxShadow: '0 8px 22px rgba(15, 43, 91, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.55) inset',
      backdropFilter: 'blur(4px)',
      display: 'none',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      width: 'fit-content',
      pointerEvents: 'auto',
      transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
    });

    const isAtBottom = () =>
      Math.abs(scrollEl.scrollHeight - scrollEl.scrollTop - scrollEl.clientHeight) <= 1;

    const ensureButtonAtEnd = () => {
      if (scrollEl.lastElementChild !== button) {
        scrollEl.appendChild(button);
      }
    };

    let rafId: number | null = null;
    let interactionUntil = 0;
    let touchY: number | null = null;
    const initialUserMessages = scrollEl.querySelectorAll('.gengage-chat-bubble--user');
    let lastUserMessage = initialUserMessages[initialUserMessages.length - 1] ?? null;
    const updateButtonVisibility = () => {
      ensureButtonAtEnd();
      const atBottom = isAtBottom();
      button.style.display = followLatest || atBottom ? 'none' : 'inline-flex';
    };

    const scheduleUpdate = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateButtonVisibility();
        if (followLatest) {
          scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'instant' });
        }
      });
    };

    const handleButtonClick = () => {
      followLatest = true;
      interactionUntil = 0;
      internalChat._drawer?.scrollToBottomPresentation?.('auto');
      scheduleUpdate();
    };

    const handleMouseEnter = () => {
      button.style.transform = 'translateY(-1px)';
      button.style.borderColor = 'rgba(15, 43, 91, 0.32)';
      button.style.boxShadow =
        '0 10px 24px rgba(15, 43, 91, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.6) inset';
    };
    const handleMouseLeave = () => {
      button.style.transform = 'translateY(0)';
      button.style.borderColor = 'rgba(15, 43, 91, 0.24)';
      button.style.boxShadow =
        '0 8px 22px rgba(15, 43, 91, 0.18), 0 0 0 1px rgba(255, 255, 255, 0.55) inset';
    };
    button.addEventListener('mouseenter', handleMouseEnter);
    button.addEventListener('mouseleave', handleMouseLeave);

    button.addEventListener('click', handleButtonClick);
    scrollEl.appendChild(button);
    ensureButtonAtEnd();

    // Content growth and keyboard/composer resizing are not user scroll gestures.
    // Only explicit interaction may pause following, never a larger scrollHeight.
    const handleInteraction = () => {
      interactionUntil = Date.now() + 1500;
    };
    const pauseFollowing = () => {
      handleInteraction();
      followLatest = false;
    };
    const handleWheel = (event: WheelEvent) => {
      handleInteraction();
      if (event.deltaY < 0) pauseFollowing();
    };
    const handleTouchStart = (event: TouchEvent) => {
      touchY = event.touches[0]?.clientY ?? null;
      handleInteraction();
    };
    const handleTouchMove = (event: TouchEvent) => {
      const nextY = event.touches[0]?.clientY ?? null;
      if (nextY !== null && touchY !== null && nextY > touchY) pauseFollowing();
      touchY = nextY;
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (['ArrowUp', 'PageUp', 'Home'].includes(event.key)) pauseFollowing();
      else if (['ArrowDown', 'PageDown', 'End', ' '].includes(event.key)) handleInteraction();
    };
    const handleScroll = () => {
      if (isAtBottom()) followLatest = true;
      else if (Date.now() < interactionUntil) followLatest = false;
      scheduleUpdate();
    };
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    scrollEl.addEventListener('wheel', handleWheel, { passive: true });
    scrollEl.addEventListener('pointerdown', handleInteraction, { passive: true });
    scrollEl.addEventListener('touchstart', handleTouchStart, { passive: true });
    scrollEl.addEventListener('touchmove', handleTouchMove, { passive: true });
    scrollEl.addEventListener('keydown', handleKeyDown);

    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(scheduleUpdate);
    resizeObserver?.observe(scrollEl);
    const observedMessages = new Set<Element>();
    const observeMessages = () => {
      for (const message of observedMessages) {
        if (!scrollEl.contains(message)) {
          resizeObserver?.unobserve(message);
          observedMessages.delete(message);
        }
      }
      for (const message of scrollEl.querySelectorAll('.gengage-chat-bubble')) {
        if (observedMessages.has(message)) continue;
        observedMessages.add(message);
        resizeObserver?.observe(message);
      }
    };
    observeMessages();
    const mutationObserver = new MutationObserver(() => {
      const userMessages = scrollEl.querySelectorAll('.gengage-chat-bubble--user');
      const newestUserMessage = userMessages[userMessages.length - 1] ?? null;
      if (newestUserMessage !== lastUserMessage) {
        lastUserMessage = newestUserMessage;
        followLatest = true;
        interactionUntil = 0;
      }
      ensureButtonAtEnd();
      observeMessages();
      scheduleUpdate();
    });
    mutationObserver.observe(scrollEl, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    scheduleUpdate();

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      scrollEl.removeEventListener('scroll', handleScroll);
      scrollEl.removeEventListener('wheel', handleWheel);
      scrollEl.removeEventListener('pointerdown', handleInteraction);
      scrollEl.removeEventListener('touchstart', handleTouchStart);
      scrollEl.removeEventListener('touchmove', handleTouchMove);
      scrollEl.removeEventListener('keydown', handleKeyDown);
      button.removeEventListener('mouseenter', handleMouseEnter);
      button.removeEventListener('mouseleave', handleMouseLeave);
      button.removeEventListener('click', handleButtonClick);
      button.remove();
    };
  };

  const attachIfNeeded = () => {
    const nextScrollEl = queryAllAcrossRoots('#gengage-chat-scroll')[0] ?? null;
    if (!nextScrollEl && currentScrollEl) {
      detachFromScrollEl();
      detachFromScrollEl = () => {};
      currentScrollEl = null;
      return;
    }
    if (!nextScrollEl || nextScrollEl === currentScrollEl) {
      return;
    }
    detachFromScrollEl();
    currentScrollEl = nextScrollEl;
    detachFromScrollEl = attachScrollOverlay(nextScrollEl);
  };

  attachIfNeeded();
  const observer = new MutationObserver(attachIfNeeded);
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  return () => {
    observer.disconnect();
    detachFromScrollEl();
    restorePresentationPatch();
    if (drawer && originalSetFocus && originalSetFormerMessagesVisible) {
      drawer.setPresentationFocus = originalSetFocus;
      drawer.setFormerMessagesButtonVisible = originalSetFormerMessagesVisible;
    }
  };
}
